import  { useEffect, useMemo, useState, memo } from "react";
import "./Dashboard.scss";
import { useInterval } from "../../hooks/hooks";
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import {
  sanitizeStatus,
  getStatusColor,
  StatusColor,
  StatusColors,
} from "../workflows/StatusChip";
import { useNavigate } from "react-router-dom";
import { MultiSelectEnv } from "../../components/environnement/MultiSelectEnv";
import { calculQueryEnv } from "../../components/environnement/utilsEnv";
import { Button } from "primereact/button";
import { TopBar } from "../../components/topBar/topBar";
import { StreamInfo, useGetWorkflowsQuery } from "../../app/apiSlice";

export const statusFlowList = ["running", "completed", "canceled", "terminated"];
export const OTHER_STATUS = 'other';

export const DashboardOverview = () => {
  const [envsSelected, setSelectedEnvs] =
    useState<{ name: string; code: string }[]>([]);

  const queryEnv = useMemo(() => {
    return calculQueryEnv(envsSelected)
  }, [envsSelected]);



  const { data, ...status } = useGetWorkflowsQuery({ env: queryEnv })

  const dashboardData = useMemo(() => {
    if(status.isError)return undefined
    return data
  },[data,status])

  const { t } = useTranslation();

  // Using this instead of satuts.isError to keep error status true while refetching and avoid blinking (when passing from fetching to error)
  const [isError, setIsError] = useState(false);



  useInterval(() => {
    // TODO use websocket ;)
    status.refetch();
  }, 1000);

  useEffect(() => {
    if (!status.isFetching) {
      setIsError(status.isError);
    }
  }, [status.isError, status.isFetching]);

  return (
    <div className="workspace">
      {/* Toolbar with title */}
      <TopBar left={<h2>{t("title.dashboardPage")}</h2>} right={undefined} />

      <>
        <div className="m-2">
          <div className="flex mb-3 align-items-center">
              <h1 className="section-title mr-2">{`${
                isError
                  ? ""
                  : status.isLoading
                  ? "..."
                  : data?.length || 0
              } ${t("dashboard.stream.configured")}`}</h1>
            {<MultiSelectEnv envsSelected={envsSelected} setSelectedEnvs={(envs) => {
              setSelectedEnvs(envs)
            }} /> }
          </div>


          <div className="grid">
            {statusFlowList.concat(OTHER_STATUS).map(status =>
              <StatusStat  
                name={t(`kube-status.${status}`)}
                status={status}
                data={dashboardData}
                queryEnv={queryEnv}
          />
          )}
          </div>


        </div>
      </>

      {/* todo: info on Nodes ? (from `data?.nodes`) */}
    </div>
  );
};

// Simple box with a title, a big number (and a color)
const StatBox = ({
  name,
  stat,
  color = StatusColors["grey"],
  env,
  status
}: {
  name: string;
  stat: number | undefined;
  color?: StatusColor;
  env: string;  
  status:string
}) => {
  const [isHover, setIsHover] = useState(false);
  const navigate = useNavigate();

  const handleMouseEnter = () => {
    setIsHover(true);
  };
  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const redirectUrl = useMemo(() => {
    let status = null
    return `/workflows?env=${env}${status ? `&state=${status}` : ""}`
  },[status,env])

  const borderStyle = useMemo(() => {
    if (isHover && stat && stat > 0) return "0.3em solid";
    return "none";
  }, [isHover]);

  if (!stat) {
    color = StatusColors["grey"];
  }


  return (
    <div className="col-4">
      <div
        className={"card" + " p-2"}
        style={{
          backgroundColor: color.background,
          color: color.text,
          outline: borderStyle,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex align-items-center justify-content-between">
          <h1>{name}</h1>
              {isHover && stat && stat > 0 ? <Button label="Voir Flux" style={{padding:"0"}} link onClick={() => navigate(redirectUrl)} /> : null}
        </div>
        <div className={classNames("value")}>{stat !== undefined ? stat : "X"}</div>
      </div>
    </div>
  );
};

export const filterFlowsByStatus = (statusToFilter: string, flowStatus: string) => {
  if (statusToFilter !== OTHER_STATUS) {
    return statusToFilter === sanitizeStatus(flowStatus);
  } else {
    return !statusFlowList.includes(sanitizeStatus(flowStatus));
  }
}

// Display number of Flow with a status in statusLst
// (use corresponding color or grey if there's 0 flow with status in statusLst)
const StatusStat = memo(function StatusStat({
  name,
  status,
  data,
  queryEnv,
}: {
  name: string;
  status: string;
  data: StreamInfo[] | undefined;
  queryEnv: string;
}) {



  const statusCount = useMemo(() => {
    if (data) {
      return data.filter((streamInfo) => filterFlowsByStatus(status, streamInfo.status)).length || 0;
    }    
    return undefined;
  }, [data]);

  return (
    <StatBox
      name={name}
      stat={statusCount}
      color={
        getStatusColor(status)
      }
      env={queryEnv}
      status={status}
    />
  );
});
